import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { debounce } from 'lodash';

import { getAllRiderInstructions } from '../../../api/services/RiderInstructions';
import * as Constants from '../../../constants';

interface IUseRiders {
  riders: RiderLocationInstructions[];
  debounceHandleQuery: (query: string) => void;
  handlePagination: () => void;
  queryParams: QueryParams;
  isLoading: boolean;
}
export const useRiders = (): IUseRiders => {
  const [riders, setRiders] = useState<RiderLocationInstructions[]>([]);
  const [queryParams, setQueryParams] = useState<QueryParams>({
    page: 1,
    perPage: Constants.ITEM_LIMIT_PER_PAGE,
    query: '',
  });
  const [isLoading, setIsLoading] = useState(true);
  const abortController = useRef<AbortController>();
  const { locationId } = useParams() as { locationId: string };

  const debounceHandleQuery = useMemo(() => debounce(handleQuery, 400), []);

  useEffect(() => {
    return () => {
      debounceHandleQuery.cancel();
      abortController.current?.abort();
    };
  }, [queryParams.query]);

  const handlePagination = () => {
    setQueryParams((prevQueryParams) => ({ ...prevQueryParams, page: prevQueryParams.page + 1 }));
  };

  function handleQuery(query) {
    if (!query) {
      setIsLoading(true);
      setRiders([]);
      setQueryParams((prevQueryParams) => ({ ...prevQueryParams, query: query, page: 1 }));
    }

    if (query !== queryParams.query && query.length >= Constants.MINIMUM_SEARCH_STRING_LENGTH) {
      setIsLoading(true);
      setRiders([]);
      setQueryParams((prevQueryParams) => ({ ...prevQueryParams, query: query, page: 1 }));
    }
  }

  const getQueryParams = useCallback(() => {
    const queryParamsToSubmit = {
      perPage: Constants.ITEM_LIMIT_PER_PAGE,
      page: queryParams.page,
      q: queryParams.query,
    };

    return queryParamsToSubmit;
  }, [queryParams]);

  const fetchRiders = useCallback(async () => {
    abortController.current = new AbortController();

    try {
      const response = await getAllRiderInstructions(getQueryParams(), locationId, abortController.current.signal);

      if (response?.type === 'success' && response.data.riders.length > 0) {
        const newRiders = response.data.riders;

        setIsLoading(newRiders.length === Constants.ITEM_LIMIT_PER_PAGE);
        setRiders((prevRiders) => prevRiders.concat(newRiders));
      } else if (!abortController.current.signal.aborted) {
        setIsLoading(false);
      }
    } catch (error) {}
  }, [getQueryParams]);

  useEffect(() => {
    fetchRiders();
  }, [fetchRiders]);

  return { riders, debounceHandleQuery, handlePagination, queryParams, isLoading };
};
